import { FC } from "react";
import { Stack, TableCell, TableRow } from "@mui/material";
import { getColorByValue } from "@/components/pages/commtiments/common/utils/helpers/grid/dataGridHelpers";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { CommitmentsProgressBar } from "@/components/pages/commtiments/common/components/CommitmentsProgressBar";
import { money } from "@/utils/numeral/money";
import { getPercentage } from "@/utils/numeral/getPercentage";
import { CommitmentsOverviewSectionLegend } from "@/components/pages/commtiments/overview/content/components/common/CommitmentsOverviewSectionLegend";
import { useHover } from "@/utils/hooks/useHover.hook";
import { CommitmentsOverviewCoverageTableViewUtilizationLink } from "@/components/pages/commtiments/overview/content/components/commitments/components/CommitmentsOverviewCommitmentsTableViewUtilizationLink";

interface CommitmentsOverviewCommitmentsTableRowProps {
  commitmentItem: {
    name: string;
    net_savings: number | null;
    used_commitment_to_date: number | null;
    total_commitment_to_date: number | null;
  };
}
export const CommitmentsOverviewCommitmentsTableRow: FC<
  CommitmentsOverviewCommitmentsTableRowProps
> = ({ commitmentItem }) => {
  const { ref, hovered } = useHover();

  const utilizationPercent = getUtilizationPercent(
    commitmentItem.used_commitment_to_date ?? 0,
    commitmentItem.total_commitment_to_date ?? 0,
  );
  const utilized = commitmentItem.used_commitment_to_date ?? 0;
  const remaining = commitmentItem.total_commitment_to_date
    ? +(commitmentItem.total_commitment_to_date - utilized).toFixed(2)
    : 0;
  const remainingPercent =
    utilizationPercent !== null ? +(100 - utilizationPercent).toFixed(2) : null;

  const color = getColorByValue(utilizationPercent);

  return (
    <TableRow ref={ref} sx={{ "&:hover": { backgroundColor: "action.hover" } }}>
      <TableCell sx={tableCellSx}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack flex={1} direction="row" alignItems="center" spacing={1}>
              <TypographyWithTooltip
                title={commitmentItem.name}
                variant="body2"
                fontWeight="bold"
              />

              {hovered && (
                <CommitmentsOverviewCoverageTableViewUtilizationLink
                  name={commitmentItem.name}
                />
              )}
            </Stack>

            <Stack spacing={0.5} direction="row" alignItems="center">
              <CommitmentsOverviewSectionLegend
                label={
                  utilizationPercent !== null ? `${utilizationPercent}%` : "N/A"
                }
                color={color}
              />
              <TypographyWithTooltip
                variant="body2"
                fontWeight="medium"
                textAlign="end"
                color="text.secondary"
                title={utilized === null ? "N/A" : `(${money(utilized)})`}
              />
            </Stack>

            <Stack spacing={0.5} direction="row" alignItems="center">
              <CommitmentsOverviewSectionLegend
                label={
                  remainingPercent !== null ? `${remainingPercent}%` : "N/A"
                }
                color="grey.400"
              />
              <TypographyWithTooltip
                variant="body2"
                fontWeight="medium"
                textAlign="end"
                color="text.secondary"
                title={remaining === null ? "N/A" : `(${money(remaining)})`}
              />
            </Stack>
          </Stack>

          <CommitmentsProgressBar
            value={utilizationPercent ?? 0}
            overrideColor={color}
            width="100%"
            hideValueLabel
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const tableCellSx = {
  py: 1.5,
  borderBottom: "none",
};

const getUtilizationPercent = (partial: number, total: number) => {
  if (total === 0) {
    return null;
  }

  return +getPercentage(partial, total).toFixed(2);
};
